import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface PaymentStatus {
  user_id: string;
  username: string;
  hasPaidPlatform: boolean;
  hasPaidPrizes: boolean;
}

const PaiementPage: React.FC = () => {
  const [dgList, setDgList] = useState<PaymentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const currentLeagueId = useUserStore((state) => state.currentLeagueId);
  const currentUserId = useUserStore((state) => state.user_id);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Vérifier si l'utilisateur est commissaire
        const { data: leagueData, error: leagueError } = await supabase
          .from('leagues')
          .select('admin_user_id, admin_user_id2')
          .eq('league_id', currentLeagueId)
          .single();
        
        if (leagueError) throw leagueError;
        
        const isMainCommissioner = leagueData.admin_user_id === Number(currentUserId);
        const isSecondCommissioner = leagueData.admin_user_id2 === Number(currentUserId);
        setIsCommissioner(isMainCommissioner || isSecondCommissioner);

        // 1. Récupérer tous les DGs de la ligue en cours
        const { data: leagueUsers, error: leagueUsersError } = await supabase
          .from('leagues_users')
          .select('user_id')
          .eq('league_id', currentLeagueId);
        if (leagueUsersError) throw leagueUsersError;

        // 2. Récupérer les informations des utilisateurs
        const userIds = leagueUsers?.map(lu => lu.user_id) || [];
        const { data: users, error: usersError } = await supabase
          .from('users')
          .select('user_id, username')
          .in('user_id', userIds);
        if (usersError) throw usersError;

        // 3. Récupérer les paiements pour la ligue en cours
        const { data: payments, error: payError } = await supabase
          .from('league_payments')
          .select('user_id, season_pay, prize_pay')
          .eq('league_id', currentLeagueId);
        if (payError) throw payError;

        // 4. Mapper les DGs avec leur statut de paiement
        const paymentMap = new Map<string, {platform: boolean, prizes: boolean}>();
        payments?.forEach((p: any) => {
          paymentMap.set(p.user_id, {
            platform: !!p.season_pay,
            prizes: !!p.prize_pay
          });
        });
        const result: PaymentStatus[] = users?.map((user: any) => {
          const payment = paymentMap.get(user.user_id) || {platform: false, prizes: false};
          return {
            user_id: user.user_id,
            username: user.username,
            hasPaidPlatform: payment.platform,
            hasPaidPrizes: payment.prizes,
          };
        }) || [];
        setDgList(result);
      } catch (err: any) {
        setError(err.message || 'Erreur lors du chargement.');
      } finally {
        setLoading(false);
      }
    };
    if (currentLeagueId) fetchData();
  }, [currentLeagueId, currentUserId]);

  const togglePrizePay = async (userId: string, currentStatus: boolean) => {
    if (!isCommissioner) {
      toast.error('Seuls les commissaires peuvent modifier les paiements des prix');
      return;
    }

    try {
      // Vérifier si l'utilisateur a payé la plateforme
      const dg = dgList.find(d => d.user_id === userId);
      if (!dg?.hasPaidPlatform) {
        toast.error('Le paiement de la plateforme doit être effectué avant de marquer les prix comme payés');
        return;
      }

      // Simplement mettre à jour le champ prize_pay
      const { error } = await supabase
        .from('league_payments')
        .update({
          prize_pay: !currentStatus
        })
        .eq('user_id', userId)
        .eq('league_id', currentLeagueId);

      if (error) throw error;

      // Mettre à jour la liste locale
      setDgList(prev => prev.map(dg => 
        dg.user_id === userId 
          ? { ...dg, hasPaidPrizes: !currentStatus }
          : dg
      ));

      toast.success(`Paiement des prix ${!currentStatus ? 'marqué comme payé' : 'marqué comme non payé'} pour ${dg.username}`);
    } catch (error: any) {
      toast.error('Erreur lors de la mise à jour : ' + error.message);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <button
          className="btn btn-link"
          onClick={() => navigate('/my-office')}
          title="Retour au bureau DG"
        >
          <ArrowLeft size={18} /> Retour
        </button>
      </div>
      
      <h2 className="text-2xl font-bold mb-4 text-center">Statut des paiements des DGs</h2>
      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger text-center">{error}</div>
      ) : (
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0">
              <div className="card-header bg-primary text-white text-center">
                <h5 className="mb-0">📊 Statut des Paiements - Saison en cours</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th className="px-4 py-3">👤 Directeur Général</th>
                        <th className="px-4 py-3 text-center">🏢 Paiement Plateforme</th>
                        <th className="px-4 py-3 text-center">🏆 Paiement Prix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dgList.map((dg) => {
                        const bothPaid = dg.hasPaidPlatform && dg.hasPaidPrizes;
                        const neitherPaid = !dg.hasPaidPlatform && !dg.hasPaidPrizes;
                        return (
                          <tr 
                            key={dg.user_id}
                            className={bothPaid ? 'table-success' : neitherPaid ? 'table-danger' : 'table-warning'}
                            style={{
                              backgroundColor: bothPaid 
                                ? 'rgba(40, 167, 69, 0.1)' 
                                : neitherPaid 
                                ? 'rgba(220, 53, 69, 0.1)'
                                : 'rgba(255, 193, 7, 0.1)',
                              borderLeft: `4px solid ${bothPaid ? '#28a745' : neitherPaid ? '#dc3545' : '#ffc107'}`
                            }}
                          >
                            <td className="px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div 
                                  className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: bothPaid ? '#28a745' : neitherPaid ? '#dc3545' : '#ffc107',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {dg.username.charAt(0).toUpperCase()}
                                </div>
                                <span className="fw-semibold">{dg.username}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {dg.hasPaidPlatform ? (
                                <span className="badge bg-success fs-6 px-3 py-2">
                                  ✅ Payé
                                </span>
                              ) : (
                                <span className="badge bg-danger fs-6 px-3 py-2">
                                  ❌ Non payé
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {dg.hasPaidPrizes ? (
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <span className="badge bg-success fs-6 px-3 py-2">
                                    ✅ Payé
                                  </span>
                                  {isCommissioner && (
                                    <button
                                      className="btn btn-sm btn-outline-warning"
                                      onClick={() => togglePrizePay(dg.user_id, dg.hasPaidPrizes)}
                                      title="Marquer comme non payé"
                                    >
                                      ↩️
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <span className="badge bg-danger fs-6 px-3 py-2">
                                    ❌ Non payé
                                  </span>
                                  {isCommissioner && dg.hasPaidPlatform && (
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => togglePrizePay(dg.user_id, dg.hasPaidPrizes)}
                                      title="Marquer comme payé"
                                    >
                                      ✅
                                    </button>
                                  )}
                                  {isCommissioner && !dg.hasPaidPlatform && (
                                    <small className="text-muted">Plateforme requis</small>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer bg-light text-center">
                <div className="row">
                  <div className="col-4">
                    <div className="text-success">
                      <strong>🏢 Plateforme: {dgList.filter(dg => dg.hasPaidPlatform).length}/{dgList.length}</strong>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="text-primary">
                      <strong>🏆 Prix: {dgList.filter(dg => dg.hasPaidPrizes).length}/{dgList.length}</strong>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="text-warning">
                      <strong>✅ Complet: {dgList.filter(dg => dg.hasPaidPlatform && dg.hasPaidPrizes).length}/{dgList.length}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaiementPage;
